import { useEffect, useRef, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { saveGame, loadGame, deleteGame, submitScore, } from "../lib/api";
const SAVE_DEBOUNCE_MS = 2000; // save 2 s after last move
export function useSaveGame(gameState, options) {
    const { isAuthenticated } = options;
    const queryClient = useQueryClient();
    const saveTimerRef = useRef(null);
    const lastSavedBoardRef = useRef("");
    // -----------------------------------------------------------------------
    // Load saved game for a given difficulty when auth is available
    // -----------------------------------------------------------------------
    const loadQuery = useQuery({
        queryKey: ["savedGame", gameState.puzzle?.difficulty],
        queryFn: () => gameState.puzzle ? loadGame(gameState.puzzle.difficulty) : null,
        enabled: isAuthenticated && gameState.status === "playing",
        staleTime: Infinity, // only re-fetch on explicit invalidation
    });
    // Trigger onLoad callback once when data arrives
    const onLoadRef = useRef(options.onLoad);
    onLoadRef.current = options.onLoad;
    useEffect(() => {
        if (loadQuery.data && onLoadRef.current) {
            onLoadRef.current(loadQuery.data);
        }
    }, [loadQuery.data]);
    // -----------------------------------------------------------------------
    // Auto-save (debounced) whenever board changes
    // -----------------------------------------------------------------------
    const saveMutation = useMutation({ mutationFn: saveGame });
    const triggerSave = useCallback(() => {
        if (!isAuthenticated || !gameState.puzzle)
            return;
        if (gameState.status !== "playing")
            return;
        const boardKey = JSON.stringify(gameState.board);
        if (boardKey === lastSavedBoardRef.current)
            return; // nothing changed
        if (saveTimerRef.current)
            clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            if (!gameState.puzzle)
                return;
            // Serialise notes: Set<number>[] → number[][]
            const notesRaw = gameState.notes.map((s) => [...s]);
            saveMutation.mutate({
                difficulty: gameState.puzzle.difficulty,
                puzzleId: gameState.puzzle.id,
                puzzle: gameState.puzzle.puzzle,
                solution: gameState.puzzle.solution,
                board: gameState.board,
                notes: notesRaw,
                timerSeconds: gameState.elapsedSeconds,
                hintsUsed: gameState.hintsUsed,
                isDaily: gameState.isDaily,
                ...(gameState.dailyDate ? { dailyDate: gameState.dailyDate } : {}),
            });
            lastSavedBoardRef.current = boardKey;
        }, SAVE_DEBOUNCE_MS);
    }, [isAuthenticated, gameState, saveMutation]);
    useEffect(() => {
        triggerSave();
    }, [gameState.board, triggerSave]);
    // -----------------------------------------------------------------------
    // Submit score + delete saved game on win
    // -----------------------------------------------------------------------
    const scoreMutation = useMutation({
        mutationFn: submitScore,
        onSuccess: () => {
            // Invalidate leaderboard + user stats caches
            queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
            queryClient.invalidateQueries({ queryKey: ["userStats"] });
            queryClient.invalidateQueries({ queryKey: ["userRank"] });
        },
    });
    const deleteMutation = useMutation({ mutationFn: deleteGame });
    useEffect(() => {
        if (gameState.status !== "won" || !gameState.puzzle || !isAuthenticated)
            return;
        const { puzzle, elapsedSeconds, hintsUsed } = gameState;
        scoreMutation.mutate({
            puzzleId: puzzle.id,
            difficulty: puzzle.difficulty,
            timeSeconds: Math.max(1, elapsedSeconds),
            hintsUsed,
            isDaily: gameState.isDaily,
            ...(gameState.dailyDate ? { dailyDate: gameState.dailyDate } : {}),
        });
        deleteMutation.mutate(puzzle.difficulty);
        // Invalidate saved game cache
        queryClient.invalidateQueries({
            queryKey: ["savedGame", puzzle.difficulty],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameState.status]);
    // -----------------------------------------------------------------------
    // Cleanup debounce timer on unmount
    // -----------------------------------------------------------------------
    useEffect(() => {
        return () => {
            if (saveTimerRef.current)
                clearTimeout(saveTimerRef.current);
        };
    }, []);
    return {
        isSaving: saveMutation.isPending,
        isSubmittingScore: scoreMutation.isPending,
        savedScore: scoreMutation.data?.score ?? null,
    };
}
